<template>
    <div class="overflow-hidden rounded-xl border border-gray-200 bg-white">
        <h2
            class="border-b border-gray-100 px-5 py-3 text-sm font-semibold text-gray-700"
        >
            {{ title }}
        </h2>

        <p v-if="rows.length === 0" class="px-5 py-4 text-sm text-gray-400">
            {{ t("vue.kiosk_ai_usage_empty") }}
        </p>

        <div v-else class="overflow-x-auto">
            <table class="w-full text-sm">
                <thead>
                    <tr class="border-b border-gray-100 text-gray-400">
                        <th class="px-5 py-2 text-left font-medium">
                            {{ entityLabel }}
                        </th>
                        <th class="px-5 py-2 text-right font-medium">
                            {{ t("vue.kiosk_ai_usage_chats") }}
                        </th>
                        <th class="px-5 py-2 text-right font-medium">
                            {{ t("vue.kiosk_ai_usage_messages") }}
                        </th>
                        <th class="px-5 py-2 text-right font-medium">
                            {{ t("vue.kiosk_ai_usage_jobs") }}
                        </th>
                        <th class="px-5 py-2 text-right font-medium">
                            {{ t("vue.kiosk_ai_usage_avg_response") }}
                        </th>
                        <th class="px-5 py-2 text-right font-medium">
                            {{ t("vue.kiosk_ai_usage_avg_cost_per_answer") }}
                        </th>
                        <th class="px-5 py-2 text-right font-medium">
                            {{ t("vue.kiosk_ai_usage_avg_cost_per_chat") }}
                        </th>
                        <th class="px-5 py-2 text-right font-medium">
                            {{ t("vue.kiosk_ai_usage_total_cost") }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="row in rows"
                        :key="row.entity_id"
                        class="border-b border-gray-50 last:border-0"
                    >
                        <td class="px-5 py-2">
                            <inertia-link
                                v-if="row.resolved"
                                :href="buildDetailUrl(row.entity_id)"
                                class="font-medium text-gray-700 hover:text-indigo-600 hover:underline"
                            >
                                {{ row.entity_name }}
                            </inertia-link>
                            <span v-else class="font-medium text-gray-700">
                                {{ row.entity_name }}
                            </span>
                            <span
                                v-if="row.attached === false"
                                class="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-500"
                            >
                                {{ t("vue.kiosk_ai_usage_detached") }}
                            </span>
                            <div
                                v-if="row.entity_detail"
                                class="text-xs text-gray-400"
                            >
                                {{ row.entity_detail }}
                            </div>
                        </td>
                        <td class="px-5 py-2 text-right text-gray-500">
                            {{ formatCount(row.metrics.chat_count) }}
                        </td>
                        <td class="px-5 py-2 text-right text-gray-500">
                            {{ formatCount(row.metrics.message_count) }}
                        </td>
                        <td class="px-5 py-2 text-right text-gray-500">
                            {{ formatCount(row.metrics.job_count) }}
                            <span
                                v-if="row.metrics.failed_job_count > 0"
                                class="text-xs text-custom-red-600"
                            >
                                ({{
                                    formatCount(row.metrics.failed_job_count)
                                }})
                            </span>
                        </td>
                        <td class="px-5 py-2 text-right text-gray-500">
                            {{
                                formatDuration(row.metrics.average_response_ms)
                            }}
                        </td>
                        <td class="px-5 py-2 text-right text-gray-500">
                            {{ formatUsd(row.metrics.average_usd_per_answer) }}
                        </td>
                        <td class="px-5 py-2 text-right text-gray-500">
                            {{ formatUsd(row.metrics.average_usd_per_chat) }}
                        </td>
                        <td
                            class="px-5 py-2 text-right font-medium text-gray-800"
                        >
                            {{ formatUsd(row.metrics.total_usd) }}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script lang="ts" setup>
import route from "ziggy-js";
import { useTranslations } from "@/composables/useTranslations";
import { formatCount, formatDuration, formatUsd } from "../formatters";
import type { IAiUsageRow } from "../types";

const props = defineProps<{
    rows: IAiUsageRow[];
    title: string;
    entityLabel: string;
    detailRouteName: string;
    detailRouteParam: string;
}>();

const { t } = useTranslations();

const buildDetailUrl = (entityId: number): string =>
    route(props.detailRouteName, { [props.detailRouteParam]: entityId });
</script>
